import { createSlice } from '@reduxjs/toolkit';
import type { WsActionsMap } from './createWsActions';
import type { WsConnectionState } from './types';

/**
 * Creates a Redux slice managing WebSocket connection state.
 * Reduces ~85 lines of identical boilerplate per channel to ~5 lines.
 *
 * @example
 * const mmSlice = createWsConnectionSlice('wsMm', mmActions);
 * export default mmSlice.reducer;
 */
export const createWsConnectionSlice = (name: string, actions: WsActionsMap) => {
  const initialWsConnectionState: WsConnectionState = {
    status: 'idle',
    error: null,
    isReconnecting: false,
  };

  const {
    actionConnect,
    actionDisconnect,
    actionOnOpen,
    actionOnClose,
    actionOnClosed,
    actionSend,
    actionOnMessage,
    actionOnReconnectBegin,
    actionOnError,
  } = actions;

  return createSlice({
    name,
    initialState: initialWsConnectionState,
    reducers: {},
    extraReducers: (builder) => {
      builder.addCase(actionConnect, (state) => {
        if (state.status === 'connected') {
          state.isReconnecting = true;
        }
        state.status = 'connecting';
      });

      builder.addCase(actionOnReconnectBegin, (state) => {
        if (state.status === 'connected' || state.status === 'idle') {
          state.isReconnecting = true;
        }
        state.status = 'connecting';
      });

      builder.addCase(actionDisconnect, (state) => {
        state.status = 'disconnecting';
      });

      builder.addCase(actionOnOpen, (state) => {
        state.status = 'connected';
        state.isReconnecting = false;
        state.error = null;
      });

      builder.addCase(actionOnClose, (state) => {
        state.status = 'idle';
      });

      builder.addCase(actionOnClosed, (state) => {
        state.status = 'idle';
      });

      builder.addCase(actionSend, () => {
        // no state changes on send
      });

      builder.addCase(actionOnMessage, () => {
        // message handling is done by consumer slices via extraReducers
      });

      builder.addCase(actionOnError, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      });
    },
  });
};
